import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { DashboardOverview } from "./dashboard/DashboardOverview";
import { BacklinksTab } from "./dashboard/BacklinksTab";
import { MyContent2Tab } from "./dashboard/MyContent2Tab";
import { LLMsTxtGenerator } from "./dashboard/LLMsTxtGenerator";
import { SeoAuditTab } from "./dashboard/SeoAuditTab";
import { GoogleIndexTab } from "./dashboard/GoogleIndexTab";
import { VisitorsTab } from "./dashboard/VisitorsTab";
import { SimpleSettingsTab } from "./dashboard/SimpleSettingsTab";
import { ArticlePreview } from "./ArticlePreview";
import { FullScreenArticlePreview } from "./FullScreenArticlePreview";
import { Button } from "@/components/ui/button";
import { Bell } from "lucide-react";
import type { User } from "@supabase/supabase-js";
import { FeedbackModal } from "./FeedbackModal";
import { identifyUser, resetUser } from "@/utils/posthog";
import { NotificationsModal } from "./NotificationsModal";
import { NotificationBadge } from "./NotificationBadge";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileNotSupported } from "./MobileNotSupported";
import { useSubscription } from "@/hooks/useSubscription";
import { SubscriptionRequiredCard } from "./SubscriptionRequiredCard";
import { trackViewContent } from "@/utils/metaPixel";

interface ProfileData {
  organization_name: string;
  industry: string;
}

interface WebsiteData {
  id: string;
  domain: string;
  title: string;
  favicon_url: string;
  analysis_status: string | null;
  analysis_started_at: string | null;
  analysis_completed_at: string | null;
  detected_niche: string | null;
}

const DashboardLayout = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true); // Initial auth loading
  const [dataLoading, setDataLoading] = useState(true); // Profile/website data loading
  const [dataLoadTimeout, setDataLoadTimeout] = useState(false); // Timeout flag for data loading
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [website, setWebsite] = useState<WebsiteData | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [showArticlePreview, setShowArticlePreview] = useState(false);
  const [selectedArticleTitle, setSelectedArticleTitle] = useState("");
  const [selectedBlogPostId, setSelectedBlogPostId] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  
  // Check subscription status
  const { hasActiveSubscription, isLoading: subscriptionLoading } = useSubscription(user);

  // Determine current tab based on route or search params
  const getCurrentTab = () => {
    const tabFromParams = searchParams.get("tab");
    if (tabFromParams) return tabFromParams;
    
    // Map routes to tabs
    switch (location.pathname) {
      case "/my-content":
        return "content";
      case "/settings":
        return "settings";
      case "/dashboard":
      default:
        return "overview";
    }
  };

  const currentTab = getCurrentTab();

  const setActiveTab = (tab: string) => {
    // Settings is always accessible, prevent other tab switching if user doesn't have subscription
    if (!hasActiveSubscription && !subscriptionLoading && tab !== "settings") {
      // Show the subscription card (it will appear via showSubscriptionGate or showExitPaywall)
      setShowExitPaywall(true);
      return;
    }

    // Map tabs to routes
    const tabToRoute: { [key: string]: string } = {
      "overview": "/dashboard",
      "content": "/my-content", 
      "settings": "/settings",
      "backlinks": "/dashboard?tab=backlinks",
      "llms-txt": "/dashboard?tab=llms-txt",
      "seo-audit": "/dashboard?tab=seo-audit",
      "google-index": "/dashboard?tab=google-index",
      "visitors": "/dashboard?tab=visitors"
    };

    const targetRoute = tabToRoute[tab] || "/dashboard";
    
    if (targetRoute.includes("?")) {
      // For tabs that stay on dashboard with params
      setSearchParams({ tab });
    } else {
      // For tabs that have their own routes
      navigate(targetRoute);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let hasHandledSession = false; // Prevent duplicate handling
    let timeoutId: NodeJS.Timeout | null = null;
    let oauthPollInterval: NodeJS.Timeout | null = null;

    // Check if we're returning from OAuth (has code param)
    const urlParams = new URLSearchParams(window.location.search);
    const hasOAuthCode = urlParams.has('code');
    const isOAuthCallback = hasOAuthCode || urlParams.has('access_token');

    // Safety timeout - ensure loading is always cleared after 3 seconds max (reduced from 5)
    // For OAuth callbacks, give a bit more time (5s) as Supabase needs to process
    timeoutId = setTimeout(() => {
      if (isMounted && loading) {
        console.warn('Dashboard loading timeout - forcing state resolution');
        setLoading(false);
        // If we have a user but no profile/website, redirect to onboarding
        if (user && (!profile || !website)) {
          navigate('/onboarding', { replace: true });
        } else if (!user) {
          navigate('/', { replace: true });
        }
      }
    }, isOAuthCallback ? 5000 : 3000);

    const handleSession = async (session: any, event?: string) => {
      if (!isMounted || hasHandledSession || !session?.user) return false;
      
      hasHandledSession = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (oauthPollInterval) clearInterval(oauthPollInterval);

      setUser(session.user);

      // Clean up OAuth params immediately
      const currentUrlParams = new URLSearchParams(window.location.search);
      const hasOAuthParams = currentUrlParams.has('code') || currentUrlParams.has('error') || currentUrlParams.has('access_token');
      
      // Handle OAuth errors gracefully
      const oauthError = currentUrlParams.get('error');
      const errorDescription = currentUrlParams.get('error_description');
      if (oauthError) {
        console.error('OAuth error:', oauthError, errorDescription);
        // Still clean up URL and continue - user can try again
      }
      
      if (hasOAuthParams) {
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
      }

      // Identify user in PostHog (non-blocking)
      identifyUser(session.user.id, {
        email: session.user.email,
        name: session.user.user_metadata?.full_name || session.user.user_metadata?.name,
      });

      // Set loading to false immediately after session confirmation - don't wait for data
      // This allows the UI to render immediately while data loads in background
      setLoading(false);

      // Load user data in background - don't block UI
      loadUserData(session.user.id).catch((error) => {
        console.error('Error loading user data:', error);
        if (isMounted) {
          setDataLoading(false);
          // On error, redirect to onboarding for new users
          navigate('/onboarding', { replace: true });
        }
      });
      
      // Track ViewContent for dashboard (non-blocking)
      const dashboardViewContentTracked = sessionStorage.getItem('dashboard_viewcontent_tracked');
      if (!dashboardViewContentTracked) {
        trackViewContent({
          content_name: 'Dashboard',
          content_category: 'Application',
        });
        sessionStorage.setItem('dashboard_viewcontent_tracked', 'true');
      }

      return true;
    };

    const initializeAuth = async () => {
      // Set up auth state listener FIRST - this fires immediately when session is available
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          if (!isMounted || hasHandledSession) return;
          
          if (!session?.user) {
            // User signed out - reset PostHog identification
            resetUser();
            if (timeoutId) clearTimeout(timeoutId);
            if (oauthPollInterval) clearInterval(oauthPollInterval);
            setLoading(false);
            navigate('/', { replace: true });
            return;
          }

          await handleSession(session, event);
        }
      );

      // Check for existing session immediately (fallback if onAuthStateChange hasn't fired yet)
      // For OAuth callbacks, poll more aggressively
      const checkSession = async (attempt = 0) => {
        if (!isMounted || hasHandledSession) {
          if (timeoutId) clearTimeout(timeoutId);
          return;
        }

        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('Error getting session:', error);
            // If OAuth callback and no session yet, keep polling (up to 3 attempts)
            if (isOAuthCallback && attempt < 3) {
              oauthPollInterval = setTimeout(() => checkSession(attempt + 1), 500);
              return;
            }
            if (isMounted) {
              if (timeoutId) clearTimeout(timeoutId);
              setLoading(false);
              navigate('/', { replace: true });
            }
            return;
          }

          // If we have a session and haven't handled it yet, process it
          if (session?.user && isMounted) {
            await handleSession(session);
          } else if (!session?.user && isMounted) {
            // If OAuth callback but no session yet, poll a few more times
            if (isOAuthCallback && attempt < 3) {
              oauthPollInterval = setTimeout(() => checkSession(attempt + 1), 500);
              return;
            }
            // No session - redirect to home
            if (timeoutId) clearTimeout(timeoutId);
            setLoading(false);
            navigate('/', { replace: true });
          }
        } catch (error) {
          console.error('Error checking session:', error);
          // If OAuth callback and error, keep polling (up to 3 attempts)
          if (isOAuthCallback && attempt < 3) {
            oauthPollInterval = setTimeout(() => checkSession(attempt + 1), 500);
            return;
          }
          if (isMounted) {
            if (timeoutId) clearTimeout(timeoutId);
            setLoading(false);
            navigate('/', { replace: true });
          }
        }
      };

      // Initial check
      checkSession();

      return () => {
        isMounted = false;
        subscription.unsubscribe();
        if (timeoutId) clearTimeout(timeoutId);
        if (oauthPollInterval) clearInterval(oauthPollInterval);
      };
    };

    initializeAuth();
  }, [navigate]);


  const loadUserData = async (userId: string): Promise<boolean> => {
    try {
      // Load profile and website in parallel for faster checking
      // Use Promise.allSettled to handle both queries simultaneously
      // Add timeout to prevent hanging - reduced from 8s to 4s for faster UX
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('Data load timeout')), 4000)
      );

      const [profileResult, websiteResult] = await Promise.race([
        Promise.allSettled([
          supabase
            .from('profiles')
            .select('organization_name, industry')
            .eq('user_id', userId)
            .single(),
          supabase
            .from('websites')
            .select('id, domain, title, favicon_url, analysis_status, analysis_started_at, analysis_completed_at, detected_niche')
            .eq('user_id', userId)
            .eq('is_primary', true)
            .single()
        ]),
        timeoutPromise
      ]) as PromiseSettledResult<any>[];

      // Check profile first
      const profileData = profileResult.status === 'fulfilled' ? profileResult.value.data : null;
      const profileError = profileResult.status === 'fulfilled' ? profileResult.value.error : null;

      if (profileError || !profileData) {
        // Profile missing - redirect immediately to onboarding
        setDataLoading(false);
        navigate('/onboarding', { replace: true });
        return false;
      }

      setProfile(profileData);

      // Check website
      const websiteData = websiteResult.status === 'fulfilled' ? websiteResult.value.data : null;
      const websiteError = websiteResult.status === 'fulfilled' ? websiteResult.value.error : null;

      if (websiteError || !websiteData) {
        // Website missing - redirect immediately to onboarding
        setDataLoading(false);
        navigate('/onboarding', { replace: true });
        return false;
      }

      setWebsite(websiteData);
      setDataLoading(false); // Data loaded successfully
      
      // Check if user returned from checkout without subscribing - do this immediately after loading data
      // This prevents the flash of the subscription card
      const checkoutKey = `going_to_checkout_${userId}`;
      const wasGoingToCheckout = sessionStorage.getItem(checkoutKey);
      const isReturningFromCheckoutSuccess = new URLSearchParams(window.location.search).get('subscription') === 'success';
      
      if (wasGoingToCheckout === 'true' && !isReturningFromCheckoutSuccess) {
        // They returned from checkout - clear flags immediately
        const onboardingKey = `onboarding_completed_${userId}`;
        sessionStorage.removeItem(onboardingKey);
        sessionStorage.removeItem(checkoutKey);
        setJustCompletedOnboarding(false);
        // Set a flag that we should show the modal (we'll verify subscription status in useEffect)
        // But set it optimistically to prevent card from showing
        setShowExitPaywall(true);
      }

      return true; // Indicate we should continue (didn't navigate away)
    } catch (error) {
      console.error('Error loading user data:', error);
      setDataLoading(false);
      // On error, try to redirect to onboarding if we don't have data
      if (!profile || !website) {
        navigate('/onboarding', { replace: true });
        return false;
      }
      return true; // If we have some data, continue
    }
  };

  // Check if returning from successful subscription
  const isReturningFromCheckout = new URLSearchParams(window.location.search).get('subscription') === 'success';
  
  // Check if user just completed onboarding (should not show paywall immediately)
  // Initialize state synchronously to prevent flash of wrong UI
  const [justCompletedOnboarding, setJustCompletedOnboarding] = useState(() => {
    if (!user) return false;
    const onboardingKey = `onboarding_completed_${user.id}`;
    return sessionStorage.getItem(onboardingKey) === 'true';
  });
  
  // Initialize showExitPaywall synchronously if user returned from checkout without subscription
  // We'll verify subscription status in useEffect, but initialize state immediately to prevent flash
  const [showExitPaywall, setShowExitPaywall] = useState(false);

  // Clear onboarding flag when subscription becomes active or when returning successfully from checkout
  useEffect(() => {
    if (user) {
      const onboardingKey = `onboarding_completed_${user.id}`;
      const checkoutKey = `going_to_checkout_${user.id}`;
      
      // Clear flags if subscription is active
      if (hasActiveSubscription) {
        sessionStorage.removeItem(onboardingKey);
        sessionStorage.removeItem(checkoutKey);
        setJustCompletedOnboarding(false);
      }
      
      // Clear checkout flag if returning successfully from checkout
      if (isReturningFromCheckout) {
        sessionStorage.removeItem(checkoutKey);
      }
    }
  }, [hasActiveSubscription, user, isReturningFromCheckout]);

  // Check for checkout return - show modal if they returned without subscription
  // This runs after subscription status is loaded to prevent showing modal if they have subscription
  useEffect(() => {
    if (!user || loading || subscriptionLoading || isReturningFromCheckout) return;
    
    // Check if user was going to checkout (they might have returned without subscribing)
    const checkoutKey = `going_to_checkout_${user.id}`;
    const wasGoingToCheckout = sessionStorage.getItem(checkoutKey);
    
    // If they were going to checkout but don't have subscription, show paywall modal
    if (wasGoingToCheckout === 'true' && !hasActiveSubscription) {
      // Flags were already cleared in loadUserData, just show the modal
      setShowExitPaywall(true);
    } else if (hasActiveSubscription && showExitPaywall) {
      // If they have subscription, don't show paywall
      setShowExitPaywall(false);
    }
  }, [user, hasActiveSubscription, isReturningFromCheckout, loading, subscriptionLoading, showExitPaywall]);

  useEffect(() => {
    if (!user) return;
    
    // Check if user just completed onboarding
    const onboardingKey = `onboarding_completed_${user.id}`;
    const completedOnboarding = sessionStorage.getItem(onboardingKey);
    
    if (completedOnboarding === 'true') {
      setJustCompletedOnboarding(true);
      // Clear the flag after 30 seconds (in case checkout redirect fails or is slow)
      // This ensures paywall shows if they're still on dashboard without subscription
      const timeout = setTimeout(() => {
        sessionStorage.removeItem(onboardingKey);
        setJustCompletedOnboarding(false);
      }, 30000); // 30 seconds
      
      return () => clearTimeout(timeout);
    }
  }, [user]);

  // Handle beforeunload - show browser confirmation if user tries to exit without subscription
  // Note: We can't show a React modal on beforeunload, so we use browser's default confirmation
  useEffect(() => {
    if (!user || hasActiveSubscription || justCompletedOnboarding) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only show browser confirmation if they don't have subscription
      if (!hasActiveSubscription && !justCompletedOnboarding) {
        // Show browser's default confirmation dialog
        e.preventDefault();
        e.returnValue = 'You need to subscribe to continue. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    // Handle browser back/forward button - show modal instead
    const handlePopState = () => {
      if (!hasActiveSubscription && !justCompletedOnboarding) {
        setShowExitPaywall(true);
        // Push state back to prevent navigation
        window.history.pushState(null, '', window.location.href);
      }
    };

    // Push a state so we can detect back button
    window.history.pushState(null, '', window.location.href);

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [user, hasActiveSubscription, justCompletedOnboarding]);

  // Show minimal loading state while profile/website data loads (after auth is confirmed)
  // Add timeout to prevent infinite loading - MUST be before any early returns
  useEffect(() => {
    if (dataLoading && user) {
      const timeout = setTimeout(() => {
        setDataLoadTimeout(true);
        setDataLoading(false);
        // If timeout and no data, redirect to onboarding
        if (!profile || !website) {
          navigate('/onboarding', { replace: true });
        }
      }, 4000); // 4 second timeout for data loading
      return () => clearTimeout(timeout);
    } else {
      setDataLoadTimeout(false);
    }
  }, [dataLoading, user, profile, website, navigate]);

  // Show loading state only during initial auth check (not during data loading)
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground font-medium">Setting up your account...</p>
          <p className="text-muted-foreground text-sm mt-2">This should only take a moment</p>
        </div>
      </div>
    );
  }

  // Show activation message while polling for subscription after checkout
  if (isReturningFromCheckout && subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Activating Your Subscription</h2>
          <p className="text-muted-foreground">Please wait while we activate your subscription...</p>
        </div>
      </div>
    );
  }

  if (!user || (dataLoading && !dataLoadTimeout) || (!profile || !website)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground text-sm">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Show mobile not supported message ONLY if user has active subscription AND is on mobile
  // Users without subscription on mobile should see subscription card (allows them to subscribe on mobile)
  if (isMobile && hasActiveSubscription) {
    return <MobileNotSupported />;
  }

  // Show subscription required card if no active subscription
  // BUT: Don't show immediately if user just completed onboarding (they're being redirected to checkout)
  // Only show if they try to exit or if they come back later without subscription
  // Also don't show if they returned from checkout without subscribing (showExitPaywall handles the modal)
  // IMPORTANT: showExitPaywall is set in useEffect, so we need to check it here
  // Also don't show if returning from successful checkout (subscription is activating)
  const showSubscriptionGate = !hasActiveSubscription && !justCompletedOnboarding && !showExitPaywall && !isReturningFromCheckout;

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background flex w-full relative">
        {/* Blur sidebar when subscription is required */}
        <div className={showSubscriptionGate && currentTab !== "settings" ? "blur-sm opacity-50 pointer-events-none transition-all duration-300" : ""}>
          <AppSidebar 
            key="sidebar-stable"
            user={user}
            website={website}
            currentTab={currentTab}
            onTabChange={setActiveTab}
            hasActiveSubscription={hasActiveSubscription}
          />
        </div>
        
        <main className="flex-1 overflow-hidden bg-gray-50/30 relative">
          {showArticlePreview ? (
            <FullScreenArticlePreview
              articleId={selectedBlogPostId}
              onClose={() => setShowArticlePreview(false)}
            />
          ) : (
            <>
              {/* Top Bar - Only show welcome message on overview tab */}
              {currentTab === "overview" && (
                <div className="bg-background/95 sticky top-0 z-40">
                  <div className="max-w-[1400px] mx-auto px-8 lg:px-12 py-10">
                    <div className="flex items-start justify-between w-full gap-4">
                      <div className="flex flex-col space-y-3 max-w-2xl flex-shrink-0 min-w-0">
                        <h1 className="text-3xl font-bold text-gray-900 tracking-tight leading-tight">
                          Welcome back, <span className="font-bold text-gray-900">{user?.user_metadata?.name || 'User'}</span>!
                        </h1>
                        <p className="text-lg text-gray-600 leading-relaxed">Here's an overview of your website's performance.</p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <Button
                          variant="outline"
                          className="rounded-xl px-6 py-2.5 text-sm font-medium border-gray-200 hover:bg-gray-50 text-gray-700 whitespace-nowrap"
                          onClick={() => setFeedbackOpen(true)}
                        >
                          Leave Feedback
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="rounded-xl border-gray-200 hover:bg-gray-50 text-gray-700 flex-shrink-0"
                          aria-label="Notifications"
                          onClick={() => setNotificationOpen(true)}
                        >
                          <NotificationBadge />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab Content with Constrained Width */}
              <div className="flex-1 overflow-auto relative">
                {/* Blurred/disabled overlay when subscription is required */}
                {(showSubscriptionGate || showExitPaywall) && currentTab !== "settings" && (
                  <>
                    {/* Dark overlay with blur */}
                    <div className="fixed inset-0 bg-background/90 backdrop-blur-lg z-[45] pointer-events-none" />
                    {/* Additional darkening layer */}
                    <div className="fixed inset-0 bg-black/20 z-[45] pointer-events-none" />
                  </>
                )}
                
                {/* Subscription card - rendered outside blurred container */}
                {/* Show the working SubscriptionRequiredCard for all cases (including when returning from checkout) */}
                {(showSubscriptionGate || showExitPaywall) && currentTab !== "settings" && (
                  <div className="fixed inset-0 flex items-center justify-center z-[60] pointer-events-none">
                    <div className="pointer-events-auto">
                      <SubscriptionRequiredCard user={user} />
                    </div>
                  </div>
                )}
                
                <div className={`max-w-6xl mx-auto px-8 lg:px-12 py-10 relative transition-all duration-300 ${(showSubscriptionGate || showExitPaywall) && currentTab !== "settings" ? "blur-md opacity-30 pointer-events-none" : ""}`}>
                  {/* Smooth tab system with proper TabsContent - forceMount keeps components mounted to preserve state */}
                  <Tabs value={currentTab} onValueChange={setActiveTab} className="h-full">
                    {/* Settings is always accessible, other tabs require subscription */}
                    {(showSubscriptionGate || showExitPaywall) && currentTab !== "settings" ? (
                      // Empty state - card is rendered above
                      <div className="min-h-[60vh]" />
                    ) : (
                      <>
                        <TabsContent value="overview" className="h-full mt-0" forceMount={true}>
                          <DashboardOverview 
                            user={user}
                            profile={profile}
                            website={website}
                            isActive={currentTab === "overview"}
                          />
                        </TabsContent>
                        
                        <TabsContent value="backlinks" className="h-full mt-0" forceMount={true}>
                          <BacklinksTab website={website} user={user} isActive={currentTab === "backlinks"} />
                        </TabsContent>
                        
                        <TabsContent value="content" className="h-full mt-0" forceMount={true}>
                          <MyContent2Tab 
                            user={user}
                            website={website}
                            onShowArticlePreview={(title: string, articleId?: string) => {
                              setSelectedArticleTitle(title);
                              setSelectedBlogPostId(articleId || '');
                              setShowArticlePreview(true);
                            }}
                            isActive={currentTab === "content"}
                          />
                        </TabsContent>
                        
                        <TabsContent value="llms-txt" className="h-full mt-0" forceMount={true}>
                          <LLMsTxtGenerator user={user} website={website} isActive={currentTab === "llms-txt"} />
                        </TabsContent>
                        
                        <TabsContent value="seo-audit" className="h-full mt-0" forceMount={true}>
                          <SeoAuditTab website={website} isActive={currentTab === "seo-audit"} />
                        </TabsContent>
                        
                        <TabsContent value="google-index" className="h-full mt-0" forceMount={true}>
                          <GoogleIndexTab website={website} isActive={currentTab === "google-index"} />
                        </TabsContent>
                        
                        <TabsContent value="visitors" className="h-full mt-0" forceMount={true}>
                          <VisitorsTab website={website} />
                        </TabsContent>
                        
                        {/* Settings is always accessible */}
                        <TabsContent value="settings" className="h-full mt-0" forceMount={true}>
                          <SimpleSettingsTab user={user} isActive={currentTab === "settings"} />
                        </TabsContent>
                      </>
                    )}
                  </Tabs>
                </div>
              </div>
            </>
          )}
        </main>
      </div>

      {/* Modals */}
      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      <NotificationsModal open={notificationOpen} onOpenChange={setNotificationOpen} />
      {/* Removed SubscriptionGateModal - using SubscriptionRequiredCard instead for consistency */}
    </SidebarProvider>
  );
};

export default DashboardLayout;